"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { QrCode, BarChart3, Euro, Shield, ExternalLink } from "lucide-react";
import gridPattern from "../assets/abstract-grid-pattern.png";
import notifMock from "../assets/mobile-notification-center.png";

export default function BookingFlow() {
  const [step, setStep] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  // Auto-rotate steps
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % steps.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  const steps = [
    {
      id: 1,
      title: "Scan or Select",
      description:
        "Scan the QR code or select a location on the map to open the operator's location directly",
      icon: QrCode,
      img: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Schritt%201-eWoybDED3SGAXMvBji6GMCC6ijSSxI.png",
    },
    {
      id: 2,
      title: "Choose Tariff",
      description:
        "Select a suitable available tariff that best fits your needs",
      icon: BarChart3,
      img: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Schritt2%20-U7I1GZNlucigTt8e88QrBfQpNhC7vk.png",
    },
    {
      id: 3,
      title: "Pay",
      description:
        "Complete your booking with a secure payment using your preferred method",
      icon: Euro,
      img: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Schritt%203-80xL96vTC4kuA8QiziL9eP4101gBxX.png",
    },
    {
      id: 4,
      title: "Use",
      description: "Tap to unlock and start using the service immediately",
      icon: Shield,
      img: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Schritt%204-MRDxb9tJ2dKlfkw4kTUOsdhCQ5HxWr.png",
    },
  ];

  const nextStep = () => {
    setStep((prev) => (prev + 1) % steps.length);
  };

  const prevStep = () => {
    setStep((prev) => (prev - 1 + steps.length) % steps.length);
  };

  // Animation for the progress line
  const progressWidth = `${(activeStep / (steps.length - 1)) * 100}%`;

  return (
    <div className="relative">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 opacity-80 "></div>

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-2xl opacity-20"></div>
        <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-2xl opacity-20"></div>
        <div
          className="absolute inset-0 bg-repeat opacity-5"
          style={{ backgroundImage: `url(${gridPattern})` }}
        ></div>
      </div>

      <div className="p-8 md:p-12">
        {/* Timeline with animated progress */}
        <div className="mb-12">
          <div className="relative flex justify-between items-center">
            {/* Gray background line */}
            <div className="absolute h-1 w-full bg-gray-200 rounded"></div>

            {/* Animated colored progress line with gradient */}
            <div
              className="absolute h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded transition-all duration-500 ease-in-out"
              style={{ width: progressWidth }}
            ></div>

            {steps.map((stepItem, index) => {
              const isActive = index <= activeStep;
              return (
                <div
                  key={stepItem.id}
                  className="relative z-10 flex flex-col items-center"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md"
                        : "bg-white text-gray-400 border border-gray-200"
                    }`}
                  >
                    <stepItem.icon className="w-5 h-5" />
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={`text-sm font-medium ${
                        isActive ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {stepItem.title}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* img Showcase - Multiple Phones */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-64 h-64 bg-gradient-to-b from-blue-100 to-white opacity-70 rounded-full blur-3xl"></div>

              {/* Multiple Phone Display */}
              <div className="relative flex items-center justify-center">
                {/* Background Phone (left) - Dark themed */}
                <div className="absolute -left-16 top-10 z-0 opacity-80 transform -rotate-12 scale-90">
                  <div className="w-[280px] h-[570px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[40px] p-3 shadow-xl">
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-[32px] overflow-hidden relative">
                      <img
                        src={notifMock}
                        alt="Mobile Notification Center"
                        width={280}
                        height={570}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                {/* Main Phone (center) */}
                <div className="relative z-10">
                  <div className="w-[320px] h-[650px] overflow-hidden relative rounded-[40px] shadow-2xl bg-inherit">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={step}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="w-full h-full"
                        onAnimationComplete={() => {
                          setActiveStep(step);
                        }}
                      >
                        <img
                          src={steps[step].img || "/placeholder.svg"}
                          alt={steps[step].title}
                          width={320}
                          height={650}
                          className="w-full h-full object-cover rounded-[40px]"
                        />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                {/* Background Phone (right) */}
                <div className="absolute -right-16 top-10 z-0 opacity-80 transform rotate-12 scale-90">
                  <div className="w-[280px] h-[570px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[40px] p-3 shadow-xl">
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-[32px] overflow-hidden relative">
                      <img
                        src={notifMock}
                        alt="Mobile Notification Center"
                        width={280}
                        height={570}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step Description */}
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mb-4">
                  <span className="text-sm font-medium text-white">
                    Step {steps[step].id}
                  </span>
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {steps[step].title}
                </h3>

                <p className="text-lg text-gray-600 mb-8">
                  {steps[step].description}
                </p>

                <div className="flex space-x-4">
                  <button
                    onClick={prevStep}
                    className="bg-white text-gray-700 px-6 py-3 rounded-full hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextStep}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-full hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-md"
                  >
                    Next
                  </button>
                </div>

                {/* App store badges and Web App button */}
                <div className="mt-8 space-y-4">
                  <div className="flex flex-wrap gap-4">
                    <a
                      href="https://apps.apple.com/at/app/juhuu/id1603108125"
                      className="transition-transform hover:scale-105"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/apple.191e8c81d9bee01b4f9a-grn8UZYrVBtIPIcaeHRW6tVtebVaMz.webp"
                        alt="Download on the App Store"
                        width={160}
                        height={53}
                        className="h-[53px] w-auto"
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.juhuu_bikebox.juhuu&pli=1"
                      className="transition-transform hover:scale-105"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/google.52f09ed88134ad81db87-Ze921iU6VIxRIZdX3lnhNwbZIiWYV3.webp"
                        alt="Get it on Google Play"
                        width={180}
                        height={53}
                        className="h-[53px] w-auto"
                      />
                    </a>
                  </div>

                  <div className="flex justify-start">
                    <a
                      href="https://app.juhuu.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-md hover:shadow-lg w-auto"
                    >
                      Open WebApp
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
